import type { MeetingBucket } from '@wundo/shared/calendar.ts';
import type { CalendarMeetingItem } from '@/lib/meetings.ts';
import { m } from '@/paraglide/messages.js';

/** One row of the "Toon" menu: a drawer, or the heading that ticks three of them. */
export type AgendaFilterValue = MeetingBucket | 'all';

/** The three drawers that hold conversations. "Alle gesprekken" is exactly these. */
export const CONVERSATION_BUCKETS: readonly MeetingBucket[] = [
  'one_on_one',
  'team',
  'external',
];

/**
 * What the agenda opens on: the conversations, and not the rest.
 *
 * Tasks and the leftovers are opt-in because they are not conversations —
 * Wundo has nothing to do with a focus block, and a week that opens full of
 * them buries the meetings the page is for. Whoever wants their whole calendar
 * back ticks them on, and the agenda remembers that in this browser.
 */
export const DEFAULT_BUCKETS: readonly MeetingBucket[] = CONVERSATION_BUCKETS;

/** Every drawer, in the order the menu lists them. */
export const ALL_BUCKETS: readonly MeetingBucket[] = [
  ...CONVERSATION_BUCKETS,
  'task',
  'other',
];

const LABEL_BY_BUCKET: Record<MeetingBucket, () => string> = {
  one_on_one: m.agenda_filter_one_on_one,
  team: m.agenda_filter_team,
  external: m.agenda_filter_external,
  task: m.agenda_filter_task,
  other: m.agenda_filter_other,
};

export function bucketLabel(bucket: MeetingBucket): string {
  return LABEL_BY_BUCKET[bucket]();
}

/**
 * The menu, as rows. "Alle gesprekken" is ticked when the three under it are,
 * and pressing it ticks all three — it never unticks anything, because a
 * heading that empties the page when pressed twice is a trap.
 */
export function filterRows(selected: readonly MeetingBucket[]): {
  value: AgendaFilterValue;
  label: string;
  checked: boolean;
  separatorBefore?: boolean;
}[] {
  return [
    {
      value: 'all',
      label: m.agenda_filter_all_conversations(),
      checked: CONVERSATION_BUCKETS.every((bucket) => selected.includes(bucket)),
    },
    ...ALL_BUCKETS.map((bucket) => ({
      value: bucket,
      label: bucketLabel(bucket),
      checked: selected.includes(bucket),
      /* A line under the heading, and another above the two that are not conversations. */
      separatorBefore: bucket === 'one_on_one' || bucket === 'task',
    })),
  ];
}

/** The new selection after one row was pressed. */
export function toggleBucket(
  selected: readonly MeetingBucket[],
  value: AgendaFilterValue,
): MeetingBucket[] {
  if (value === 'all') {
    return ALL_BUCKETS.filter(
      (bucket) => CONVERSATION_BUCKETS.includes(bucket) || selected.includes(bucket),
    );
  }

  return selected.includes(value)
    ? selected.filter((bucket) => bucket !== value)
    : ALL_BUCKETS.filter((bucket) => bucket === value || selected.includes(bucket));
}

/**
 * What the button reads. The set is named where it has a name — everything,
 * the conversations, one drawer — and counted where it has none, because
 * spelling three of five out loud on a toolbar button is unreadable.
 */
export function filterLabel(selected: readonly MeetingBucket[]): string {
  if (selected.length === 0) return m.agenda_filter_none();
  if (selected.length === ALL_BUCKETS.length) return m.agenda_filter_all_items();
  if (
    selected.length === CONVERSATION_BUCKETS.length &&
    CONVERSATION_BUCKETS.every((bucket) => selected.includes(bucket))
  ) {
    return m.agenda_filter_all_conversations();
  }
  if (selected.length === 1) return bucketLabel(selected[0]);
  return m.agenda_filter_count({ count: selected.length });
}

/**
 * Whether the filter lets this meeting through.
 *
 * A meeting the user wrote down themselves always shows. It is on the agenda
 * because they put it there, and hiding it behind a drawer they never chose it
 * into would make it disappear with no way of guessing which tick brings it
 * back.
 *
 * So does busy time the organisation's agendaproxy blocked. Wundo cannot tell
 * what it is, so no drawer is its own; hiding it would leave a hole in the week.
 */
export function passesFilter(
  meeting: CalendarMeetingItem,
  selected: readonly MeetingBucket[],
): boolean {
  return (
    meeting.source === 'manual' || meeting.blocked || selected.includes(meeting.bucket)
  );
}
